// WeatherImpact.js - Weather effects on flight operations
// Determines delays and ground stops based on weather conditions

// Tomorrow.io weather codes for severe weather
const SEVERE_WEATHER_CODES = [
  8000, // Thunderstorm
  8001, // Thunderstorm with light rain
  8003, // Thunderstorm with heavy rain
  7101, // Heavy ice pellets
  7102, // Light ice pellets
  5101, // Heavy snow
  7000, // Ice pellets
  6201, // Heavy freezing rain
];

/**
 * Assess weather impact on flight operations
 * @param {Object} weatherData - Tomorrow.io weather data values
 * @returns {Object} Impact assessment with canDepart, delayMinutes, reason
 */
export function assessWeatherImpact(weatherData) {
  const impact = {
    canDepart: true,
    delayMinutes: 0,
    reason: null,
    severity: 'clear' // clear, advisory, delay, grounded
  };

  if (!weatherData) {
    return impact; // No data = assume clear
  }

  const {
    windSpeed = 0,           // m/s
    visibility = 16,         // km (Tomorrow.io default, ~10 miles)
    precipitationIntensity = 0, // mm/hr
    weatherCode = 1000
  } = weatherData;

  // Convert wind speed from m/s to knots (1 m/s = 1.944 knots)
  const windKnots = windSpeed * 1.944;

  // Convert visibility from km to miles (Tomorrow.io returns km, 1 km = 0.621371 miles)
  const visibilityMiles = visibility * 0.621371;

  // Wind delays (knots)
  if (windKnots > 45) {
    impact.canDepart = false;
    impact.reason = 'High winds - flights grounded';
    impact.severity = 'grounded';
    return impact;
  } else if (windKnots > 35) {
    impact.delayMinutes += 30;
    impact.reason = 'Wind delay';
    impact.severity = 'delay';
  } else if (windKnots > 25) {
    impact.delayMinutes += 15;
    impact.reason = 'Wind advisory';
    impact.severity = impact.severity === 'clear' ? 'advisory' : impact.severity;
  }

  // Visibility delays (miles)
  if (visibilityMiles < 0.25) {
    impact.canDepart = false;
    impact.reason = 'Zero visibility - airport closed';
    impact.severity = 'grounded';
    return impact;
  } else if (visibilityMiles < 1) {
    impact.delayMinutes += 45;
    impact.reason = impact.reason ? `${impact.reason}, Low visibility` : 'Low visibility delay';
    impact.severity = 'delay';
  } else if (visibilityMiles < 3) {
    impact.delayMinutes += 15;
    impact.reason = impact.reason ? `${impact.reason}, Reduced visibility` : 'Reduced visibility';
    impact.severity = impact.severity === 'clear' ? 'advisory' : impact.severity;
  }

  // Precipitation delays (mm/hr)
  if (precipitationIntensity > 10) {
    impact.delayMinutes += 30;
    impact.reason = impact.reason ? `${impact.reason}, Heavy precipitation` : 'Heavy precipitation';
    impact.severity = 'delay';
  } else if (precipitationIntensity > 5) {
    impact.delayMinutes += 15;
    impact.reason = impact.reason ? `${impact.reason}, Moderate precipitation` : 'Moderate precipitation';
    impact.severity = impact.severity === 'clear' ? 'advisory' : impact.severity;
  }

  // Severe weather codes (thunderstorm, etc.)
  if (SEVERE_WEATHER_CODES.includes(weatherCode)) {
    impact.canDepart = false;
    impact.reason = 'Severe weather - ground stop';
    impact.severity = 'grounded';
    return impact;
  }

  return impact;
}

/**
 * Get weather status color for UI
 * @param {string} severity - Weather severity level
 * @returns {string} Color code
 */
export function getWeatherStatusColor(severity) {
  switch (severity) {
    case 'grounded': return '#ef4444'; // red
    case 'delay': return '#f59e0b'; // amber
    case 'advisory': return '#eab308'; // yellow
    case 'clear': return '#22c55e'; // green
    default: return '#6b7280'; // gray
  }
}

/**
 * Get weather status label for UI
 * @param {string} severity - Weather severity level
 * @returns {string} Human-readable label
 */
export function getWeatherStatusLabel(severity) {
  switch (severity) {
    case 'grounded': return 'Ground Stop';
    case 'delay': return 'Delays';
    case 'advisory': return 'Advisory';
    case 'clear': return 'Clear';
    default: return 'Unknown';
  }
}

/**
 * Format delay time for display
 * @param {number} minutes - Delay in minutes
 * @returns {string} Formatted string
 */
export function formatDelay(minutes) {
  if (minutes === 0) return 'On Time';
  if (minutes < 60) return `+${minutes}min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `+${hours}h ${mins}m` : `+${hours}h`;
}

export default {
  assessWeatherImpact,
  getWeatherStatusColor,
  getWeatherStatusLabel,
  formatDelay,
  SEVERE_WEATHER_CODES
};
